import React, { useState } from "react";
import { Panel } from "./FrequencyChart";
import { getBacktest, getBacktestCompare, GAME_THEME } from "../lib/api";
import { Button } from "./ui/button";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from "recharts";
import { History, Loader2, TrendingUp, TrendingDown, LayoutGrid } from "lucide-react";

const STRATEGIES = [
  { v: "hot", label: "Hot" },
  { v: "overdue", label: "Overdue" },
  { v: "cold", label: "Cold" },
];
const STRAT_LABEL = { hot: "Hot", overdue: "Overdue", cold: "Cold" };

// Stable chart-style constants (avoid re-creating objects on every render)
const CHART_MARGIN = { top: 4, right: 4, left: -24, bottom: 0 };
const COMPARE_MARGIN = { top: 8, right: 8, left: -24, bottom: 0 };
const AXIS_TICK = { fill: "#71717a", fontSize: 11 };
const AXIS_TICK_SM = { fill: "#52525b", fontSize: 10 };
const AXIS_TICK_LG = { fill: "#a1a1aa", fontSize: 12 };
const CURSOR = { fill: "rgba(255,255,255,0.04)" };
const TOOLTIP_STYLE = { background: "#09090b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 };

const Stat = ({ label, value, sub, color }) => (
  <div className="rounded-xl bg-zinc-950/60 border border-zinc-800 p-4">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{label}</div>
    <div className="font-mono font-bold text-2xl" style={{ color: color || "#fafafa" }}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

export const BacktestCard = ({ game }) => {
  const t = GAME_THEME[game];
  const [mode, setMode] = useState("single"); // single | compare
  const [strategy, setStrategy] = useState("hot");
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSingle = async (strat) => {
    setMode("single");
    setStrategy(strat);
    setLoading(true);
    setResult(null);
    try { setResult(await getBacktest(game, strat, 150)); }
    finally { setLoading(false); }
  };

  const runCompare = async () => {
    setMode("compare");
    setLoading(true);
    setCompare(null);
    try { setCompare(await getBacktestCompare(game, 150)); }
    finally { setLoading(false); }
  };

  const specialName = game === "mega_millions" ? "Mega Ball" : "Powerball";

  return (
    <Panel title="Backtesting — Would trends have helped?" testid="backtest-card"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-full p-0.5 bg-zinc-950 border border-zinc-800">
            {STRATEGIES.map((s) => (
              <button key={s.v} onClick={() => runSingle(s.v)} data-testid={`backtest-strategy-${s.v}`}
                className="px-3 py-1 rounded-full text-xs font-semibold transition-colors duration-200"
                style={mode === "single" && result && strategy === s.v
                  ? { backgroundColor: t.color, color: "#09090b" } : { color: "#71717a" }}>
                {s.label}
              </button>
            ))}
          </div>
          <button onClick={runCompare} data-testid="backtest-compare-toggle"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200"
            style={mode === "compare"
              ? { backgroundColor: "#A855F7", color: "#fff", borderColor: "#A855F7" }
              : { color: "#a1a1aa", borderColor: "#3f3f46" }}>
            <LayoutGrid size={13} /> Compare all
          </button>
        </div>
      }>
      {!result && !compare && !loading && (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <History size={24} className="text-zinc-600 mb-3" />
          <p className="text-sm text-zinc-400 max-w-md mb-4">
            Simulate picking a <span style={{ color: t.color }}>trend-based set</span> before each of the last 150 draws,
            using only the data known at that time, then compare it to what actually came up.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => runSingle(strategy)} data-testid="run-backtest"
              className="rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border border-zinc-700 h-10 px-6">
              Run backtest
            </Button>
            <Button onClick={runCompare} data-testid="run-backtest-compare"
              className="rounded-full bg-purple-600 hover:bg-purple-500 text-white h-10 px-6">
              <LayoutGrid size={15} className="mr-1.5" /> Compare all 3
            </Button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center justify-center py-14 text-zinc-500">
          <Loader2 size={24} className="animate-spin mb-3" style={{ color: t.color }} />
          <p className="text-sm">{mode === "compare" ? "Simulating all three strategies…" : "Simulating 150 historical draws…"}</p>
        </div>
      )}

      {/* SINGLE MODE */}
      {mode === "single" && result && !loading && (
        <SingleResult result={result} strategy={strategy} color={t.color} specialName={specialName} />
      )}

      {/* COMPARE MODE */}
      {mode === "compare" && compare && !loading && (
        <CompareResult compare={compare} color={t.color} specialName={specialName} />
      )}
    </Panel>
  );
};

const SingleResult = ({ result, strategy, color, specialName }) => {
  const beatsRandom = result.avg_main_matches >= result.random_expected_matches;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Avg matches / draw" value={result.avg_main_matches}
          sub={`over ${result.tested_draws} draws`} color={color} />
        <Stat label="Random baseline" value={result.random_expected_matches} sub="expected by luck" />
        <Stat label="Edge vs random"
          value={`${result.edge_pct > 0 ? "+" : ""}${result.edge_pct}%`}
          color={beatsRandom ? "#22c55e" : "#FF3B30"}
          sub={beatsRandom ? "beats chance" : "no real edge"} />
        <Stat label={`${specialName} hit rate`}
          value={`${result.special_hit_rate}%`} sub={`vs ${result.special_random_rate}% random`} />
      </div>

      <div className="flex items-start gap-2 rounded-xl bg-zinc-950/50 border border-zinc-800 p-3">
        {beatsRandom ? <TrendingUp size={16} className="text-green-500 mt-0.5" /> : <TrendingDown size={16} className="text-[#FF3B30] mt-0.5" />}
        <p className="text-xs text-zinc-400">
          {beatsRandom
            ? `The "${strategy}" strategy slightly out-matched pure chance in this sample — but the gap is tiny. Lottery draws are random; treat this as trivia, not a system.`
            : `The "${strategy}" strategy performed about the same as (or worse than) random picks. This is expected — lottery draws are random and no trend improves your odds.`}
        </p>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Match distribution</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={result.distribution} margin={CHART_MARGIN}>
            <XAxis dataKey="matches" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
            <Tooltip cursor={CURSOR} contentStyle={TOOLTIP_STYLE}
              formatter={(v, n, p) => [`${v} draws (${p.payload.pct}%)`, `${p.payload.matches} matched`]} />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {result.distribution.map((d) => (
                <Cell key={d.matches} fill={color} fillOpacity={0.35 + (d.matches / result.distribution.length) * 0.65} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {result.best_hit && (
        <div className="text-xs text-zinc-500 font-mono">
          Best hit: {result.best_hit.matches} matches on {result.best_hit.draw_date} · picked [{result.best_hit.candidate.join(", ")}] vs [{result.best_hit.actual.join(", ")}]
        </div>
      )}
    </div>
  );
};

const CompareResult = ({ compare, color, specialName }) => (
  <div className="space-y-5" data-testid="backtest-compare-view">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left">
            <th className="py-2 font-semibold">Strategy</th>
            <th className="py-2 font-semibold text-right">Avg matches</th>
            <th className="py-2 font-semibold text-right">Edge vs random</th>
            <th className="py-2 font-semibold text-right">{specialName} hit</th>
            <th className="py-2 font-semibold text-right">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {compare.strategies.map((s) => {
            const beats = s.avg_main_matches >= compare.random_expected_matches;
            return (
              <tr key={s.strategy} data-testid={`compare-row-${s.strategy}`}
                className="border-t border-zinc-800">
                <td className="py-2.5 font-semibold text-zinc-200">{STRAT_LABEL[s.strategy]}</td>
                <td className="py-2.5 text-right font-mono" style={{ color }}>{s.avg_main_matches}</td>
                <td className="py-2.5 text-right font-mono" style={{ color: beats ? "#22c55e" : "#FF3B30" }}>
                  {s.edge_pct > 0 ? "+" : ""}{s.edge_pct}%
                </td>
                <td className="py-2.5 text-right font-mono text-zinc-300">{s.special_hit_rate}%</td>
                <td className="py-2.5 text-right">
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full"
                    style={beats ? { color: "#22c55e", background: "rgba(34,197,94,0.12)" }
                                  : { color: "#FF3B30", background: "rgba(255,59,48,0.12)" }}>
                    {beats ? "beats chance" : "no edge"}
                  </span>
                </td>
              </tr>
            );
          })}
          <tr className="border-t border-zinc-800/70">
            <td className="py-2.5 text-zinc-500">Random baseline</td>
            <td className="py-2.5 text-right font-mono text-zinc-500">{compare.random_expected_matches}</td>
            <td className="py-2.5 text-right font-mono text-zinc-600">—</td>
            <td className="py-2.5 text-right font-mono text-zinc-500">{compare.special_random_rate}%</td>
            <td className="py-2.5 text-right text-zinc-600 text-xs">by luck</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">
        Avg main matches per draw vs random baseline
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={compare.strategies.map((s) => ({ name: STRAT_LABEL[s.strategy], avg: s.avg_main_matches }))}
          margin={COMPARE_MARGIN}>
          <XAxis dataKey="name" tick={AXIS_TICK_LG} axisLine={false} tickLine={false} />
          <YAxis tick={AXIS_TICK_SM} axisLine={false} tickLine={false} />
          <Tooltip cursor={CURSOR} contentStyle={TOOLTIP_STYLE} />
          <ReferenceLine y={compare.random_expected_matches} stroke="#71717a" strokeDasharray="4 4"
            label={{ value: "random", position: "right", fill: "#71717a", fontSize: 10 }} />
          <Bar dataKey="avg" radius={[5, 5, 0, 0]}>
            {compare.strategies.map((s) => (
              <Cell key={s.strategy} fill={s.avg_main_matches >= compare.random_expected_matches ? "#22c55e" : color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>

    <p className="text-xs text-zinc-500 italic">
      Across {compare.sample} draws, all three strategies land near the random baseline — the takeaway is that
      no trend reliably beats chance. This view is for insight and entertainment only.
    </p>
  </div>
);
